/**
 * BillingHistoryPage — shows the user's Stripe invoices inline.
 *
 * Design: Figma node 11370:1653
 *
 * Each row: icon square (green ✓ / red ✗) · label + sub-line · amount + status text.
 * Tapping a row opens the hosted Stripe invoice in a new tab.
 */

import React from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { useI18n } from '@/contexts/I18nContext';
import { useInvoices } from '../hooks/useInvoices';
import type { StripeInvoice } from '../services/invoiceApi';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style:                'currency',
      currency:             currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${currency.toUpperCase()} ${(cents / 100).toFixed(2)}`;
  }
}

/** Short date: "5 mai 2026" */
function formatShortDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString(undefined, {
    day:   'numeric',
    month: 'short',
    year:  'numeric',
  });
}

/** Month name only: "Maio" — year context comes from the sub-line date */
function formatMonthName(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString(undefined, {
    month: 'long',
  });
}

function isPaidStatus(status: string | null): boolean {
  return status === 'paid';
}

// ── Row label / sub-line derivation ──────────────────────────────────────────

function getRowLabel(invoice: StripeInvoice, t: (k: string) => string): string {
  const month = formatMonthName(invoice.period_start);
  return `${t('premium.billing.row_plan_label')} · ${month}`;
}

function getRowSublabel(invoice: StripeInvoice, t: (k: string) => string): string {
  const date = formatShortDate(invoice.created);

  if (!isPaidStatus(invoice.status) && invoice.failure_message) {
    return `${date} · ${invoice.failure_message}`;
  }

  if (invoice.card_brand && invoice.card_last4) {
    return `${date} · ${invoice.card_brand.toUpperCase()} •••• ${invoice.card_last4}`;
  }

  return date;
}

// ── Invoice row ───────────────────────────────────────────────────────────────

function InvoiceRow({ invoice }: { invoice: StripeInvoice }) {
  const { t } = useI18n();
  const paid   = isPaidStatus(invoice.status);
  const amount = paid ? invoice.amount_paid : invoice.amount_due;
  const url    = invoice.hosted_invoice_url ?? invoice.invoice_pdf;

  const handleTap = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      type="button"
      onClick={handleTap}
      disabled={!url}
      className="w-full text-left flex items-center gap-[14px] px-6 py-[14px] border-b border-white/[0.06] hover:bg-white/[0.03] transition-colors disabled:cursor-default"
    >
      {/* Status icon */}
      <div
        className={`shrink-0 w-[34px] h-[34px] rounded-[10px] flex items-center justify-center ${
          paid
            ? 'bg-[rgba(34,197,94,0.14)]'
            : 'bg-[rgba(239,68,68,0.14)]'
        }`}
      >
        {paid
          ? <Check size={16} className="text-[#22c55e]" strokeWidth={2.5} />
          : <X     size={16} className="text-[#ef4444]" strokeWidth={2.5} />}
      </div>

      {/* Label + sub-line */}
      <div className="flex-1 min-w-0 flex flex-col gap-[2px]">
        <p className="text-[13px] font-semibold text-white leading-[1.3] truncate">
          {getRowLabel(invoice, t)}
        </p>
        <p className="text-[11px] text-[#8d8d8d] leading-[1.3] truncate">
          {getRowSublabel(invoice, t)}
        </p>
      </div>

      {/* Amount + status */}
      <div className="shrink-0 text-right flex flex-col gap-[5px]">
        <p className="text-[14px] font-bold text-white leading-none">
          {formatMoney(amount, invoice.currency)}
        </p>
        <p
          className={`text-[9px] font-bold uppercase tracking-[0.6px] leading-none ${
            paid ? 'text-[#22c55e]' : 'text-[#ef4444]'
          }`}
        >
          {paid ? t('premium.billing.status_paid') : t('premium.billing.status_failed')}
        </p>
      </div>
    </button>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BillingHistoryPage() {
  const { t } = useI18n();
  const { invoices, isLoading, error } = useInvoices();

  return (
    <SubPageLayout title={t('premium.billing.title')} hideBottomNav>
      {/*
        -mx-4 escapes SubPageLayout's px-4 so rows sit flush with the screen edge.
        Each row then applies its own px-6 (24 px per side).
      */}
      <div className="-mx-4 pb-12 flex flex-col">

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center gap-3 px-6 py-8 text-white/40">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">{t('premium.billing.loading')}</span>
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <p className="px-6 py-8 text-sm text-red-400">{error}</p>
        )}

        {/* Empty */}
        {!isLoading && !error && invoices.length === 0 && (
          <p data-testid="billing-empty" className="px-6 py-8 text-sm text-white/40">
            {t('premium.billing.empty')}
          </p>
        )}

        {/* Invoice list */}
        {invoices.length > 0 && (
          <ul data-testid="billing-list">
            {invoices.map((inv) => (
              <li key={inv.id} data-testid="billing-row">
                <InvoiceRow invoice={inv} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </SubPageLayout>
  );
}
